import { ref, type Ref } from 'vue'
import i18n from '@/locales'
import {
  createVector4,
  getAutodeskViewing,
  getThree,
  invalidateViewer,
  type ToolbarButton,
  type Vector4,
  type ViewerInstance,
  type ViewerMouseEvent,
  type ViewerState,
  type ViewerTool
} from '@/helpers/autodeskViewer'
import {
  getLibraryNodeChanges,
  getLibraryToolbarControls,
  type LibraryToolbarControl
} from '@/helpers/libraryViewerToolbar'

type LibraryTransientViewerState = {
  explodeScale: number
  hiddenDbIds: number[]
  isolatedDbIds: number[]
}

type LibraryAssemblyOrderingViewerState = {
  ghosting: boolean
  hiddenDbIds: number[]
  isolatedDbIds: number[]
}

type LibraryViewerProps = {
  libraryEnableExplode?: boolean
  libraryMobileLayout?: boolean
  libraryMode?: boolean
  librarySelectionRedirects?: Record<number, number>
  libraryShowAdvancedSelectionControls?: boolean
}

type Options = {
  props: LibraryViewerProps
  getViewer: () => ViewerInstance | null
  viewerContainer: Ref<HTMLElement | null>
  libraryToolbarHost: Ref<HTMLElement | null>
  hideToolbarControl: (controlId: string) => void
  onSelectionActivated: (dbId: number) => void
}

const librarySelectionActivationToolName = 'Viewing.Library.SelectionActivation.Tool'

function createEmptyLibraryTransientState (): LibraryTransientViewerState {
  return {
    explodeScale: 0,
    hiddenDbIds: [],
    isolatedDbIds: []
  }
}

function cloneLibraryTransientState (state: LibraryTransientViewerState): LibraryTransientViewerState {
  return {
    explodeScale: state.explodeScale,
    hiddenDbIds: [...state.hiddenDbIds],
    isolatedDbIds: [...state.isolatedDbIds]
  }
}

export default function useAutodeskLibraryViewer ({
  props,
  getViewer,
  viewerContainer,
  libraryToolbarHost,
  hideToolbarControl,
  onSelectionActivated
}: Options) {
  const libraryMobileToolbarVisible = ref(false)
  let libraryAssetReady = false
  let pendingLibrarySelectionDbId: number | null | undefined
  let libraryExternalIsolatedDbIds: number[] | null = null
  let librarySavedCameraState: ViewerState | null = null
  let librarySavedTransientState = createEmptyLibraryTransientState()
  let libraryTransientState = createEmptyLibraryTransientState()
  let libraryUndoStack: LibraryTransientViewerState[] = []
  let libraryRedoStack: LibraryTransientViewerState[] = []
  let librarySearchFocusedDbIds: number[] = []
  let librarySearchPreviousGhosting: boolean | null = null
  let librarySearchPreviousHiddenDbIds: number[] | null = null
  let librarySearchPreviousIsolatedDbIds: number[] | null = null
  let librarySearchPreviousTheming: Map<number, Vector4> | null = null
  let libraryAssemblyOrderingPreviousState: LibraryAssemblyOrderingViewerState | null = null
  const libraryToolbarButtons: Partial<Record<LibraryToolbarControl, ToolbarButton>> = {}

  function getGhostingPreference () {
    const ghosting = getViewer()?.prefs?.get('ghosting')
    return typeof ghosting === 'boolean' ? ghosting : true
  }

  function handleLibrarySelectionActivation (event: ViewerMouseEvent) {
    if (!props.libraryMode) return false

    const viewer = getViewer()
    const canvasX = event.canvasX
    const canvasY = event.canvasY
    if (canvasX === undefined || canvasY === undefined) return false

    const dbId = viewer?.clientToWorld?.(canvasX, canvasY)?.dbId
    const selectionDbId = dbId === undefined
      ? undefined
      : props.librarySelectionRedirects?.[dbId] ?? dbId
    pendingLibrarySelectionDbId = selectionDbId ?? null
    const selectedDbIds = viewer?.getSelection?.() ?? []
    if (selectionDbId !== undefined &&
      selectedDbIds.length === 1 &&
      selectedDbIds[0] === selectionDbId) {
      onSelectionActivated(selectionDbId)
    }

    if (dbId !== undefined && selectionDbId !== undefined && selectionDbId !== dbId) {
      if (selectedDbIds.length !== 1 || selectedDbIds[0] !== selectionDbId) {
        viewer?.select?.([selectionDbId])
      } else {
        pendingLibrarySelectionDbId = undefined
      }
      return true
    }

    // Preserve Autodesk's normal selection and highlighting behavior.
    return false
  }

  const librarySelectionActivationTool: ViewerTool = {
    activate: () => undefined,
    deactivate: () => undefined,
    getName: () => librarySelectionActivationToolName,
    getNames: () => [librarySelectionActivationToolName],
    getPriority: () => 100,
    handleButtonDown: () => false,
    handleButtonUp: () => false,
    handleMouseMove: () => false,
    handleSingleClick: (event, button) => {
      return button === 0 && handleLibrarySelectionActivation(event)
    },
    handleSingleTap: (event) => {
      return handleLibrarySelectionActivation(event)
    }
  }

  function activateSelectionActivationTool () {
    const toolController = getViewer()?.toolController
    toolController?.registerTool(librarySelectionActivationTool)
    toolController?.activateTool(librarySelectionActivationToolName)
  }

  function consumeDirectSelection (selectedDbIds: number[]) {
    const directSelection = !!props.libraryMode && (
      (selectedDbIds.length === 1 && selectedDbIds[0] === pendingLibrarySelectionDbId) ||
      (selectedDbIds.length === 0 && pendingLibrarySelectionDbId === null)
    )
    pendingLibrarySelectionDbId = undefined
    return directSelection
  }

  function applyLibraryTransientState (state: LibraryTransientViewerState) {
    const viewer = getViewer()
    const hiddenChanges = getLibraryNodeChanges(
      libraryTransientState.hiddenDbIds,
      state.hiddenDbIds
    )
    if (hiddenChanges.removedDbIds.length > 0) {
      viewer?.show?.(hiddenChanges.removedDbIds)
    }
    if (hiddenChanges.addedDbIds.length > 0) {
      viewer?.hide?.(hiddenChanges.addedDbIds)
    }

    const isolatedChanges = getLibraryNodeChanges(
      libraryTransientState.isolatedDbIds,
      state.isolatedDbIds
    )
    const isolationChanged = isolatedChanges.addedDbIds.length > 0 ||
      isolatedChanges.removedDbIds.length > 0
    if (isolationChanged && libraryTransientState.isolatedDbIds.length === 0) {
      libraryExternalIsolatedDbIds = [...(viewer?.getIsolatedNodes?.() ?? [])]
    }
    if (isolationChanged && state.isolatedDbIds.length > 0) {
      viewer?.isolate?.(state.isolatedDbIds)
    } else if (isolationChanged) {
      viewer?.isolate?.(libraryExternalIsolatedDbIds ?? [])
      libraryExternalIsolatedDbIds = null
    }

    if (state.explodeScale !== libraryTransientState.explodeScale) {
      viewer?.explode?.(state.explodeScale)
    }
    libraryTransientState = cloneLibraryTransientState(state)
    updateLibraryToolbarButtonStates()
  }

  function performLibraryToolbarAction (nextState: LibraryTransientViewerState) {
    libraryUndoStack.push(cloneLibraryTransientState(libraryTransientState))
    libraryRedoStack = []
    applyLibraryTransientState(nextState)
  }

  function handleLibraryHome () {
    if (!libraryAssetReady) return

    libraryUndoStack = []
    libraryRedoStack = []
    applyLibraryTransientState(librarySavedTransientState)
  }

  function handleLibraryHide () {
    if (!libraryAssetReady || !props.libraryShowAdvancedSelectionControls) return

    const viewer = getViewer()
    const selectedDbIds = viewer?.getSelection?.() ?? []
    const hiddenDbIds = new Set(viewer?.getHiddenNodes?.() ?? [])
    const toolbarHiddenDbIds = selectedDbIds.filter(dbId => !hiddenDbIds.has(dbId))
    if (toolbarHiddenDbIds.length === 0) return

    const nextState = cloneLibraryTransientState(libraryTransientState)
    nextState.hiddenDbIds = [...new Set([...nextState.hiddenDbIds, ...toolbarHiddenDbIds])]
    performLibraryToolbarAction(nextState)
    viewer?.select?.(selectedDbIds)
  }

  function handleLibraryIsolate () {
    if (!libraryAssetReady || !props.libraryShowAdvancedSelectionControls) return

    const viewer = getViewer()
    const selectedDbIds = viewer?.getSelection?.() ?? []
    if (selectedDbIds.length === 0) return

    const nextState = cloneLibraryTransientState(libraryTransientState)
    nextState.isolatedDbIds = [...selectedDbIds]
    performLibraryToolbarAction(nextState)
    viewer?.select?.(selectedDbIds)
  }

  function handleLibraryExplode () {
    if (!libraryAssetReady || !props.libraryEnableExplode) return

    const nextState = cloneLibraryTransientState(libraryTransientState)
    nextState.explodeScale = nextState.explodeScale === 0 ? 1 : 0
    performLibraryToolbarAction(nextState)
  }

  function handleLibraryUndo () {
    if (!libraryAssetReady) return

    const previousState = libraryUndoStack.pop()
    if (!previousState) return

    libraryRedoStack.push(cloneLibraryTransientState(libraryTransientState))
    applyLibraryTransientState(previousState)
  }

  function handleLibraryRedo () {
    if (!libraryAssetReady) return

    const nextState = libraryRedoStack.pop()
    if (!nextState) return

    libraryUndoStack.push(cloneLibraryTransientState(libraryTransientState))
    applyLibraryTransientState(nextState)
  }

  function handleLibraryResetCamera () {
    if (!libraryAssetReady || !librarySavedCameraState) return

    getViewer()?.restoreState?.(librarySavedCameraState, { viewport: true }, true)
  }

  function setLibraryToolbarButtonEnabled (control: LibraryToolbarControl, enabled: boolean) {
    const buttonState = getAutodeskViewing()?.UI?.ButtonState
    const button = libraryToolbarButtons[control]
    if (!buttonState || !button?.setState) return

    button.setState(enabled ? buttonState.INACTIVE : buttonState.DISABLED)
    button.container?.setAttribute('aria-disabled', String(!enabled))
  }

  function updateLibraryToolbarButtonStates () {
    const hasSelection = (getViewer()?.getSelection?.().length ?? 0) > 0
    setLibraryToolbarButtonEnabled('hide', libraryAssetReady && hasSelection)
    setLibraryToolbarButtonEnabled('isolate', libraryAssetReady && hasSelection)
    setLibraryToolbarButtonEnabled('undo', libraryAssetReady && libraryUndoStack.length > 0)
    setLibraryToolbarButtonEnabled('redo', libraryAssetReady && libraryRedoStack.length > 0)
    setLibraryToolbarButtonEnabled('home', libraryAssetReady)
    setLibraryToolbarButtonEnabled('explode', libraryAssetReady)
    setLibraryToolbarButtonEnabled('resetCamera', libraryAssetReady && !!librarySavedCameraState)
  }

  function addLibraryToolbarButton (
    control: LibraryToolbarControl,
    tooltip: string,
    onClick: () => void
  ) {
    const viewing = getAutodeskViewing()
    const navTools = getViewer()?.getToolbar?.()?.getControl?.('navTools')
    const controlId = `library-${control}`

    if (!viewing?.UI?.Button || !navTools?.addControl || navTools.getControl?.(controlId)) return

    const button = new viewing.UI.Button(controlId)
    button.addClass?.(`library-toolbar-${control}`)
    button.setToolTip?.(tooltip)
    button.onClick = onClick
    navTools.addControl(button)
    libraryToolbarButtons[control] = button

    const buttonElement = button.container ??
      viewerContainer.value?.querySelector<HTMLElement>(`#${controlId}`)
    buttonElement?.setAttribute('aria-label', tooltip)
    buttonElement?.setAttribute('title', tooltip)
  }

  function configureLibraryToolbar () {
    const toolbar = getViewer()?.getToolbar?.()
    toolbar?.setVisible?.(false)
    toolbar?.getControl?.('modelTools')?.setVisible?.(false)
    toolbar?.getControl?.('settingsTools')?.setVisible?.(false)

    const nativeToolbarControlIds = [
      'toolbar-cameraSubmenuTool',
      'toolbar-fitToViewTool',
      'toolbar-orbitTools',
      'toolbar-panTool',
      'toolbar-zoomTool'
    ]
    nativeToolbarControlIds.forEach(hideToolbarControl)

    const controlHandlers: Record<LibraryToolbarControl, () => void> = {
      explode: handleLibraryExplode,
      hide: handleLibraryHide,
      home: handleLibraryHome,
      isolate: handleLibraryIsolate,
      redo: handleLibraryRedo,
      resetCamera: handleLibraryResetCamera,
      undo: handleLibraryUndo
    }
    const controlTooltips: Record<LibraryToolbarControl, string> = {
      explode: i18n.global.t('threeD.explodeParts'),
      hide: i18n.global.t('threeD.hideSelections'),
      home: i18n.global.t('home'),
      isolate: i18n.global.t('threeD.isolateSelections'),
      redo: i18n.global.t('threeD.redo'),
      resetCamera: i18n.global.t('threeD.resetCamera'),
      undo: i18n.global.t('threeD.undo')
    }

    getLibraryToolbarControls(
      !!props.libraryEnableExplode,
      !!props.libraryShowAdvancedSelectionControls
    ).forEach(control => {
      addLibraryToolbarButton(control, controlTooltips[control], controlHandlers[control])
    })
    if (props.libraryMobileLayout && libraryToolbarHost.value) {
      const nativeViewerScope = viewerContainer.value?.matches('.adsk-viewing-viewer')
        ? viewerContainer.value
        : viewerContainer.value?.querySelector<HTMLElement>('.adsk-viewing-viewer')
      for (const themeClass of ['light-theme', 'dark-theme']) {
        libraryToolbarHost.value?.classList.toggle(
          themeClass,
          nativeViewerScope?.classList.contains(themeClass) ?? false
        )
      }
      const toolbarElement = toolbar?.container ??
        viewerContainer.value?.querySelector<HTMLElement>('.adsk-toolbar')
      if (toolbarElement) libraryToolbarHost.value.appendChild(toolbarElement)
    }
    updateLibraryToolbarButtonStates()
    toolbar?.setVisible?.(libraryAssetReady)
  }

  function setLibraryAssetReady () {
    const viewer = getViewer()
    if (!props.libraryMode || !viewer) return false

    libraryAssetReady = true
    libraryMobileToolbarVisible.value = true
    libraryAssemblyOrderingPreviousState = null
    libraryExternalIsolatedDbIds = null
    librarySavedCameraState = viewer.getState?.({ viewport: true }) ?? null
    librarySavedTransientState = {
      explodeScale: viewer.getExplodeScale?.() ?? 0,
      hiddenDbIds: [],
      isolatedDbIds: []
    }
    libraryTransientState = cloneLibraryTransientState(librarySavedTransientState)
    libraryUndoStack = []
    libraryRedoStack = []
    updateLibraryToolbarButtonStates()
    viewer.getToolbar?.()?.setVisible?.(true)
    return true
  }

  function clearLibraryAssemblyOrderingFocus () {
    const viewer = getViewer()
    const previousState = libraryAssemblyOrderingPreviousState
    if (!previousState || !viewer) return false

    viewer.isolate?.(previousState.isolatedDbIds)
    if (previousState.hiddenDbIds.length > 0) {
      viewer.hide?.(previousState.hiddenDbIds)
    }
    viewer.setGhosting?.(previousState.ghosting)
    libraryAssemblyOrderingPreviousState = null
    invalidateViewer(viewer)
    return true
  }

  function setLibraryAssemblyOrderingFocus (childElementIds: number[]) {
    const viewer = getViewer()
    if (!libraryAssetReady || !viewer) return false

    const dbIds = [...new Set(childElementIds)].filter(dbId => Number.isInteger(dbId))
    if (dbIds.length === 0) return false

    if (libraryAssemblyOrderingPreviousState === null) {
      libraryAssemblyOrderingPreviousState = {
        ghosting: getGhostingPreference(),
        hiddenDbIds: [...(viewer.getHiddenNodes?.() ?? [])],
        isolatedDbIds: [...(viewer.getIsolatedNodes?.() ?? [])]
      }
    }

    viewer.setGhosting?.(true)
    viewer.isolate?.(dbIds)
    invalidateViewer(viewer)
    return true
  }

  function clearLibrarySearchTheming () {
    const viewer = getViewer()
    librarySearchFocusedDbIds.forEach((dbId) => {
      viewer?.setThemingColor?.(dbId, null, viewer.model, true)
    })
    librarySearchPreviousTheming?.forEach((color, dbId) => {
      viewer?.setThemingColor?.(dbId, createVector4(color), viewer.model, false)
    })
    librarySearchFocusedDbIds = []
  }

  function captureLibrarySearchTheming () {
    const currentTheming = getViewer()?.model?.getFragmentList?.().db2ThemingColor ?? {}
    return new Map(Object.entries(currentTheming).flatMap(([dbId, color]) => {
      const numericDbId = Number(dbId)
      return Number.isInteger(numericDbId)
        ? [[numericDbId, { ...color }] as [number, Vector4]]
        : []
    }))
  }

  function clearLibrarySearchFocus () {
    const viewer = getViewer()
    clearLibrarySearchTheming()
    librarySearchPreviousTheming = null
    if (librarySearchPreviousIsolatedDbIds) {
      viewer?.isolate?.(librarySearchPreviousIsolatedDbIds)
      librarySearchPreviousIsolatedDbIds = null
    }
    if (librarySearchPreviousHiddenDbIds) {
      if (librarySearchPreviousHiddenDbIds.length > 0) {
        viewer?.hide?.(librarySearchPreviousHiddenDbIds)
      }
      librarySearchPreviousHiddenDbIds = null
    }
    if (librarySearchPreviousGhosting !== null) {
      viewer?.setGhosting?.(librarySearchPreviousGhosting)
      librarySearchPreviousGhosting = null
    }
    invalidateViewer(viewer)
  }

  function setLibrarySearchFocus (elementIds: number[]) {
    const viewer = getViewer()
    if (!libraryAssetReady || !viewer) return false

    const dbIds = [...new Set(elementIds)].filter(dbId => Number.isInteger(dbId))
    if (dbIds.length === 0) {
      clearLibrarySearchFocus()
      return false
    }

    if (librarySearchPreviousIsolatedDbIds === null) {
      librarySearchPreviousIsolatedDbIds = [...(viewer.getIsolatedNodes?.() ?? [])]
      librarySearchPreviousHiddenDbIds = [...(viewer.getHiddenNodes?.() ?? [])]
      librarySearchPreviousTheming = captureLibrarySearchTheming()
      librarySearchPreviousGhosting = getGhostingPreference()
    }
    clearLibrarySearchTheming()
    viewer.clearSelection?.()
    viewer.setGhosting?.(true)
    viewer.isolate?.(dbIds)

    const THREE = getThree()
    if (THREE) {
      const searchResultColor = new THREE.Vector4(0, 0.65, 0.25, 1)
      dbIds.forEach((dbId) => {
        viewer.setThemingColor?.(dbId, searchResultColor, viewer.model, true)
      })
    }
    librarySearchFocusedDbIds = dbIds
    invalidateViewer(viewer)
    return true
  }

  return {
    libraryMobileToolbarVisible,
    activateSelectionActivationTool,
    consumeDirectSelection,
    configureLibraryToolbar,
    updateLibraryToolbarButtonStates,
    setLibraryAssetReady,
    clearLibraryAssemblyOrderingFocus,
    setLibraryAssemblyOrderingFocus,
    clearLibrarySearchFocus,
    setLibrarySearchFocus
  }
}
